import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { LinkClicksTable } from '@/components/marketing/report/LinkClicksTable';
import { RecipientsTable } from '@/components/marketing/report/RecipientsTable';
import { ReportStatTiles } from '@/components/marketing/report/ReportStatTiles';
import { ReportTimeline } from '@/components/marketing/report/ReportTimeline';
import { SkippedBreakdown } from '@/components/marketing/report/SkippedBreakdown';
import { StepVariantTable } from '@/components/marketing/report/StepVariantTable';
import { Badge, EmptyState, PageHeader, Panel, PanelHeader, Skeleton } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { CAMPAIGN_STATUS_LABEL, campaignStatusTone } from '@/lib/marketing';
import type { CampaignReport, MarketingCampaign, MarketingCampaignStatus } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * Marketing → a campaign's report. `GET /marketing/campaigns/:id` for the
 * header, `GET /marketing/campaigns/:id/report` for the figures, and the
 * recipients list paginated on its own.
 *
 * ⚠️ **THE ROUTE CARRIES NO GUARD**, so a reader without
 * `marketing-campaigns.read` reaches this component and meets its own refusal
 * panel — and issues NO request, because an empty report would read as "this
 * campaign reached nobody", a claim about the business rather than the grant.
 *
 * While the campaign is live (sending, scheduled or paused) the page refetches
 * every 30 seconds, skipping ticks while the tab is hidden and catching up the
 * moment it becomes visible again. A refresh keeps the figures on screen.
 */

const MODULE = 'marketing-campaigns' as const;
const REFRESH_MS = 30_000;
const LIVE_STATUSES: ReadonlySet<MarketingCampaignStatus> = new Set(['sending', 'scheduled', 'paused']);

/* A neutral text link in the header actions; `TextLink` owns colour, underline and focus. */
const BACK_LINK_CLASS = 'inline-flex h-9 shrink-0 items-center text-sm font-medium max-sm:h-11';

function ChartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

type LoadError = { kind: 'forbidden' | 'missing' | 'failed'; message: string };

function classify(caught: unknown): LoadError {
  if (caught instanceof ApiError) {
    if (caught.status === 403) return { kind: 'forbidden', message: caught.message };
    if (caught.status === 404) return { kind: 'missing', message: caught.message };
    return { kind: 'failed', message: caught.formMessage ?? caught.message };
  }
  return { kind: 'failed', message: caught instanceof Error ? caught.message : 'Failed to load the report' };
}

export function CampaignReportPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead(MODULE);
  const params = useParams();
  const id = Number(params.id);
  const validId = Number.isInteger(id) && id > 0;

  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!canRead || !validId) return;
    const controller = new AbortController();
    Promise.all([
      api.getMarketingCampaign(id, controller.signal),
      api.getMarketingCampaignReport(id, controller.signal),
    ])
      .then(([campaignResponse, reportResponse]) => {
        setCampaign(campaignResponse.data);
        setReport(reportResponse.data);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(classify(caught));
      });
    return () => controller.abort();
  }, [canRead, validId, id, refreshKey]);

  const live = campaign !== null && LIVE_STATUSES.has(campaign.status);

  useEffect(() => {
    if (!live) return;
    const bump = () => setRefreshKey((k) => k + 1);
    // Ticks are skipped while the tab is hidden, and the page catches up the
    // moment it is shown again — an hour-hidden tab must not show hour-old figures.
    const handle = window.setInterval(() => {
      if (!document.hidden) bump();
    }, REFRESH_MS);
    const onVisibility = () => {
      if (!document.hidden) bump();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [live]);

  /* ── Refusal ─────────────────────────────────────────────────────────── */

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Campaign report" />
        <Panel aria-labelledby="campaign-report-denied-heading">
          <PanelHeader id="campaign-report-denied-heading" title="Campaign report" />
          <EmptyState
            icon={<ChartIcon />}
            title="Not available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  if (!validId || loadError?.kind === 'missing' || loadError?.kind === 'forbidden') {
    const forbidden = loadError?.kind === 'forbidden';
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader
          eyebrow="B2B Marketing"
          title="Campaign report"
          actions={
            <TextLink to="/marketing/campaigns" className={BACK_LINK_CLASS}>
              All campaigns
            </TextLink>
          }
        />
        <Panel aria-labelledby="campaign-report-missing-heading">
          <PanelHeader id="campaign-report-missing-heading" title="Campaign report" />
          <EmptyState
            icon={<ChartIcon />}
            title={forbidden ? 'Not available to your account' : 'Campaign not found'}
            description={
              forbidden
                ? loadError?.message || 'Your permission set does not include this. An administrator can change that.'
                : 'It may have been deleted.'
            }
          />
        </Panel>
      </div>
    );
  }

  const initialLoad = campaign === null && loadError === null;
  const sender = campaign?.emailAccount ? (
    <>
      {campaign.fromNameOverride ?? campaign.emailAccount.name} &lt;
      <EmailLink address={campaign.emailAccount.fromEmail} />
      &gt;
    </>
  ) : null;

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow="B2B Marketing · Report"
        title={campaign ? campaign.name : initialLoad ? <Skeleton className="h-6 w-56" /> : 'Campaign report'}
        description={
          campaign ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Badge tone={campaignStatusTone(campaign.status)} dot>
                {CAMPAIGN_STATUS_LABEL[campaign.status]}
              </Badge>
              <span className="[overflow-wrap:anywhere]">{sender ?? 'No sending account'}</span>
              {campaign.startedAt && <span className="tabular">Started {formatDateTime(campaign.startedAt)}</span>}
              {campaign.completedAt && (
                <span className="tabular">Completed {formatDateTime(campaign.completedAt)}</span>
              )}
            </span>
          ) : undefined
        }
        actions={
          <TextLink to={`/marketing/campaigns/${id}`} className={BACK_LINK_CLASS}>
            Open campaign
          </TextLink>
        }
      />

      {loadError && (
        <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the report.</>}>
          <span className="min-w-0 [overflow-wrap:anywhere]">{loadError.message}</span>
        </Notice>
      )}

      {(report !== null || initialLoad) && <ReportStatTiles totals={report?.totals ?? null} />}

      {report && (
        <>
          <Panel className="min-w-0" aria-labelledby="report-timeline-heading">
            <PanelHeader id="report-timeline-heading" title="First 72 hours" />
            <ReportTimeline timeline={report.timeline} />
          </Panel>

          <Panel className="min-w-0" aria-labelledby="report-steps-heading">
            <PanelHeader id="report-steps-heading" title="Steps and variants" />
            <StepVariantTable steps={report.steps} />
          </Panel>

          <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Panel className="min-w-0" aria-labelledby="report-links-heading">
              <PanelHeader id="report-links-heading" title="Link clicks" />
              <LinkClicksTable links={report.links} />
            </Panel>
            <Panel className="min-w-0" aria-labelledby="report-skipped-heading">
              <PanelHeader
                id="report-skipped-heading"
                title="Skipped"
                description={`${report.totals.skipped.toLocaleString()} ${report.totals.skipped === 1 ? 'send' : 'sends'}`}
              />
              <SkippedBreakdown skippedByReason={report.skippedByReason} />
            </Panel>
          </div>
        </>
      )}

      {campaign && (
        <Panel className="min-w-0" aria-labelledby="report-recipients-heading">
          <PanelHeader id="report-recipients-heading" title="Recipients" />
          <RecipientsTable campaignId={id} refreshKey={refreshKey} />
        </Panel>
      )}
    </div>
  );
}
